import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';

import styles from './SearchableSelect.module.css';

const POP_MAX_H = 330;
const POP_MIN_W = 230;
const POP_GAP = 4;

export interface SearchableOption {
  value: string;
  label: string;
  meta?: string;
}

export interface SearchableSelectProps {
  options: SearchableOption[];
  value: string | null;
  onChange: (next: string | null) => void;
  placeholder?: string;
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  ariaLabel?: string;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = '—',
  clearable = false,
  clearLabel = 'не выбрано',
  disabled = false,
  fullWidth = false,
  ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = value != null ? (options.find((o) => o.value === value) ?? null) : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || (o.meta ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  const focusSearch = useCallback((node: HTMLInputElement | null) => node?.focus(), []);

  // список раскрывается наружу: карточки UI-кита режут содержимое по overflow,
  // поэтому позиция считается вручную и ставится fixed
  const place = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    const width = Math.max(POP_MIN_W, rect.width);
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const below = window.innerHeight - rect.bottom;
    const up = below < POP_MAX_H && rect.top > below;
    setPos({
      width,
      left,
      maxHeight: Math.min(POP_MAX_H, Math.max(140, (up ? rect.top : below) - 12)),
      ...(up ? { bottom: window.innerHeight - rect.top + POP_GAP } : { top: rect.bottom + POP_GAP }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [active, query]);

  const pick = (next: string | null) => {
    onChange(next);
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((a) => Math.max(a - 1, clearable ? -1 : 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active < 0) pick(null);
      else if (filtered[active]) pick(filtered[active].value);
    }
  };

  return (
    <div
      className={[styles.root, fullWidth && styles.full].filter(Boolean).join(' ')}
      ref={rootRef}
    >
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className={selected ? styles.value : `${styles.value} ${styles.placeholder}`}>
          {selected ? selected.label : placeholder}
        </span>
      </button>

      {open && pos && (
        <div className={styles.pop} style={pos}>
          <input
            ref={focusSearch}
            className={styles.search}
            placeholder="поиск…"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <ul className={styles.list} role="listbox" ref={listRef}>
            {clearable && (
              <li
                className={[
                  styles.item,
                  active === -1 && styles.active,
                  value === null && styles.sel,
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="option"
                data-active={active === -1}
                aria-selected={value === null}
                onMouseEnter={() => setActive(-1)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(null)}
              >
                {clearLabel}
              </li>
            )}
            {filtered.map((option, index) => (
              <li
                key={option.value}
                className={[
                  styles.item,
                  active === index && styles.active,
                  value === option.value && styles.sel,
                ]
                  .filter(Boolean)
                  .join(' ')}
                role="option"
                data-active={active === index}
                aria-selected={value === option.value}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(option.value)}
              >
                {option.label}
                {option.meta ? ` · ${option.meta}` : ''}
              </li>
            ))}
            {filtered.length === 0 && <li className={styles.empty}>ничего не найдено</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
